import { useEffect, useRef, useState } from 'react';

const DEFAULT_FORM_HEIGHT = 500;
const ANCHOR_VISIBILITY_CHECK_MS = 300;
const DEFAULT_MARGIN = 8;
const VIEWPORT_EDGE_MARGIN = 16;

export interface UsePickerFormPortalPositionOptions {
    formHeight?: number;
    margin?: number;
}

export interface UsePickerFormPortalPositionResult {
    portalStyle: React.CSSProperties;
    openUpwards: boolean;
}

/**
 * Hook do pozycjonowania formularza pickera (NewCompanyForm / NewUserForm) w portalu.
 * Oblicza pozycję fixed względem viewport: poniżej lub powyżej anchora w zależności od dostępnej przestrzeni.
 */
export function usePickerFormPortalPosition(
    anchorRef: React.RefObject<HTMLElement | null> | undefined,
    isVisible: boolean,
    options?: UsePickerFormPortalPositionOptions
): UsePickerFormPortalPositionResult {
    const formHeight = options?.formHeight ?? DEFAULT_FORM_HEIGHT;
    const margin = options?.margin ?? DEFAULT_MARGIN;

    const [result, setResult] = useState<UsePickerFormPortalPositionResult>({
        portalStyle: { visibility: 'hidden' },
        openUpwards: false
    });

    useEffect(() => {
        if (!isVisible || !anchorRef?.current || typeof document === 'undefined') {
            setResult({
                portalStyle: { visibility: 'hidden', position: 'fixed' as const },
                openUpwards: false
            });
            return;
        }

        const anchorRect = anchorRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - anchorRect.bottom;
        const spaceAbove = anchorRect.top;

        const openUpwards = spaceBelow < formHeight + margin && spaceAbove >= spaceBelow;

        const desiredTop = openUpwards
            ? anchorRect.top - formHeight - margin
            : anchorRect.bottom + margin;
        const minTop = VIEWPORT_EDGE_MARGIN;
        const maxTop = Math.max(
            VIEWPORT_EDGE_MARGIN,
            window.innerHeight - formHeight - VIEWPORT_EDGE_MARGIN
        );
        const clampedTop = Math.min(Math.max(desiredTop, minTop), maxTop);

        setResult({
            portalStyle: {
                position: 'fixed',
                top: clampedTop,
                left: anchorRect.left,
                width: anchorRect.width,
                zIndex: 999999,
                visibility: 'visible'
            },
            openUpwards
        });
    }, [isVisible, anchorRef, formHeight, margin]);

    return result;
}

export function useCloseFormWhenAnchorHidden(
    anchorRef: React.RefObject<HTMLElement | null> | undefined,
    isVisible: boolean,
    usePortal: boolean,
    closeHandler: () => void
): void {
    const closeHandlerRef = useRef(closeHandler);
    closeHandlerRef.current = closeHandler;

    useEffect(() => {
        if (!isVisible || !usePortal || !anchorRef?.current || typeof document === 'undefined') {
            return;
        }
        const anchor = anchorRef.current;
        const id = setInterval(() => {
            if (!document.body.contains(anchor) || anchor.offsetParent === null) {
                closeHandlerRef.current();
            }
        }, ANCHOR_VISIBILITY_CHECK_MS);
        return () => clearInterval(id);
    }, [isVisible, usePortal, anchorRef]);
}
